// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — HMGP Store (Zustand)
// Manages HMGP projects and subapplications
// Uses existing paProjects table with programType='hmgp' discriminator
// Subapplications stored in dedicated hmgpSubapplications table
// ═══════════════════════════════════════════════════════════════

import { create } from 'zustand';
import { TABLES, FIELDS, fetchTable, createRecord, updateRecord, deleteRecord, WORKER_URL } from '../lib/api-switch';
import { useAuthStore } from './authStore';
import type { HMGPProject, HMGPSubapplication } from '../types/pagrants';

// ── Table / field keys ───────────────────────────────────────
// HMGP projects reuse paProjects table with programType='hmgp'
const TP = TABLES.paProjects;
const FP = FIELDS.paProjects;

// Subapplications — will need a real Ignatius table key once created
// For now, use KV config store as interim storage
const SUBAPP_CONFIG_KEY = 'hmgp-subapplications';

function getToken() {
  return useAuthStore.getState().token ?? undefined;
}

function s(row: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && v !== '') return String(v);
  }
  return '';
}

// ── Row mapper for HMGP projects ─────────────────────────────
function mapHMGPProject(row: Record<string, unknown>): HMGPProject {
  const rid = Number(row.id) || 0;
  return {
    id: rid, Rid: rid,
    organizationId: s(row, 'organizationId', 'organization_id'),
    disasterId: s(row, 'disasterId', 'disaster_id'),
    projectNumber: s(row, 'projectNumber', 'project_number'),
    projectTitle: s(row, 'projectTitle', 'project_title'),
    projectType: s(row, 'category', 'projectType', 'project_type'),
    projectAddress: s(row, 'projectAddress', 'project_address'),
    projectCity: s(row, 'projectCity', 'project_city'),
    projectCounty: s(row, 'projectCounty', 'project_county'),
    projectState: s(row, 'projectState', 'project_state'),
    projectZip: s(row, 'projectZip', 'project_zip'),
    projectLat: s(row, 'projectLat', 'project_lat'),
    projectLng: s(row, 'projectLng', 'project_lng'),
    problemDescription: s(row, 'damageDescription', 'damage_description'), // reuse PA field
    scopeOfWork: s(row, 'scopeOfWork', 'scope_of_work'),
    estimatedCost: s(row, 'estimatedCost', 'estimated_cost'),
    federalSharePct: s(row, 'federalSharePct', 'federal_share_pct'),
    federalShareAmount: s(row, 'federalShareAmount', 'federal_share_amount'),
    applicantShareAmount: s(row, 'applicantShareAmount', 'applicant_share_amount'),
    otherFunding: s(row, 'otherFunding', 'other_funding'),
    otherFundingSource: s(row, 'otherFundingSource', 'other_funding_source'),
    bcaRequired: s(row, 'bcaRequired', 'bca_required'),
    bcaMethodology: s(row, 'bcaMethodology', 'bca_methodology'),
    bcaRatio: s(row, 'hazardMitigationBca', 'hazard_mitigation_bca'), // reuse PA field
    pvBenefits: s(row, 'pvBenefits', 'pv_benefits'),
    pvCosts: s(row, 'pvCosts', 'pv_costs'),
    usefulLife: s(row, 'usefulLife', 'useful_life'),
    discountRate: s(row, 'discountRate', 'discount_rate'),
    bcaToolVersion: s(row, 'bcaToolVersion', 'bca_tool_version'),
    bcaCompletedDate: s(row, 'bcaCompletedDate', 'bca_completed_date'),
    ehpStatus: s(row, 'ehpStatus', 'ehp_status'),
    ehpCompletedDate: s(row, 'ehpCompletedDate', 'ehp_completed_date'),
    nepaLevel: s(row, 'nepaLevel', 'nepa_level'),
    nepaStatus: s(row, 'nepaStatus', 'nepa_status'),
    section106Required: s(row, 'section106Required', 'section106_required'),
    section106Status: s(row, 'section106Status', 'section106_status'),
    numberOfStructures: s(row, 'numberOfStructures', 'number_of_structures'),
    structureType: s(row, 'structureType', 'structure_type'),
    preDisasterValue: s(row, 'preDisasterValue', 'pre_disaster_value'),
    floodZone: s(row, 'floodZone', 'flood_zone'),
    repetitiveLoss: s(row, 'repetitiveLoss', 'repetitive_loss'),
    severeRepetitiveLoss: s(row, 'severeRepetitiveLoss', 'severe_repetitive_loss'),
    subapplicationId: s(row, 'subapplicationId', 'subapplication_id'),
    subapplicationDate: s(row, 'subapplicationDate', 'subapplication_date'),
    statePriorityRank: s(row, 'statePriorityRank', 'state_priority_rank'),
    femaAppNumber: s(row, 'femaAppNumber', 'fema_app_number'),
    femaAwardDate: s(row, 'femaAwardDate', 'fema_award_date'),
    awardAmount: s(row, 'awardAmount', 'award_amount'),
    obligatedAmount: s(row, 'obligatedAmount', 'obligated_amount'),
    drawdownAmount: s(row, 'drawdownAmount', 'drawdown_amount'),
    performancePeriodStart: s(row, 'performancePeriodStart', 'performance_period_start'),
    performancePeriodEnd: s(row, 'performancePeriodEnd', 'performance_period_end'),
    quarterlyReportDue: s(row, 'quarterlyReportDue', 'quarterly_report_due'),
    phase: s(row, 'phase'),
    status: s(row, 'status'),
    assignedSpecialist: s(row, 'assignedSpecialist', 'assigned_specialist'),
    notes: s(row, 'notes'),
    creatorId: s(row, 'creatorId', 'creator_id'),
    createdDate: s(row, 'createdDate', 'created_date'),
    lastModifiedBy: s(row, 'lastModifiedBy', 'last_modified_by'),
    lastModifiedDate: s(row, 'lastModifiedDate', 'last_modified_date'),
  };
}

// ── Types ────────────────────────────────────────────────────

interface HMGPState {
  projects: HMGPProject[];
  subapplications: HMGPSubapplication[];
  loading: boolean;
  loadingSubs: boolean;

  fetchProjects: () => Promise<void>;
  fetchSubapplications: () => Promise<void>;
  createProject: (data: Partial<HMGPProject>) => Promise<HMGPProject | null>;
  updateProject: (rid: number, data: Partial<HMGPProject>) => Promise<void>;
  deleteProject: (rid: number) => Promise<void>;
  createSubapplication: (data: Partial<HMGPSubapplication>) => Promise<HMGPSubapplication | null>;
  updateSubapplication: (rid: number, data: Partial<HMGPSubapplication>) => Promise<void>;
  deleteSubapplication: (rid: number) => Promise<void>;
}

// ── Store ────────────────────────────────────────────────────

export const useHMGPStore = create<HMGPState>((set, get) => ({
  projects: [],
  subapplications: [],
  loading: false,
  loadingSubs: false,

  // ── Fetch HMGP projects (filter paProjects by workType='hmgp') ──
  fetchProjects: async () => {
    set({ loading: true });
    try {
      const rows = await fetchTable(TP, { token: getToken() });
      // Filter to only HMGP projects — use workType field as programType discriminator
      const hmgpRows = rows.filter((r: Record<string, unknown>) => {
        const wt = s(r, 'workType', 'work_type');
        return wt === 'hmgp';
      });
      set({ projects: hmgpRows.map(mapHMGPProject) });
    } catch (e) {
      console.error('HMGP fetchProjects error:', e);
    } finally {
      set({ loading: false });
    }
  },

  // ── Fetch subapplications from KV config (interim) ──
  fetchSubapplications: async () => {
    set({ loadingSubs: true });
    try {
      const resp = await fetch(
        `${WORKER_URL}/api/config/${SUBAPP_CONFIG_KEY}`,
        { headers: { 'Origin': 'https://apps.opengov.com' } }
      );
      if (resp.ok) {
        const data = await resp.json();
        set({ subapplications: Array.isArray(data) ? data : [] });
      } else {
        set({ subapplications: [] });
      }
    } catch (e) {
      console.error('HMGP fetchSubapplications error:', e);
      set({ subapplications: [] });
    } finally {
      set({ loadingSubs: false });
    }
  },

  // ── Create HMGP project (in paProjects with workType='hmgp') ──
  createProject: async (data) => {
    try {
      const fields: [string, string][] = [
        [FP.projectNumber, data.projectNumber || ''],
        [FP.projectTitle, data.projectTitle || ''],
        [FP.workType, 'hmgp'],  // discriminator
        [FP.category, data.projectType || ''],  // reuse category for HMGP project type
        [FP.organizationId, data.organizationId || ''],
        [FP.disasterId, data.disasterId || ''],
        [FP.damageDescription, data.problemDescription || ''],
        [FP.scopeOfWork, data.scopeOfWork || ''],
        [FP.estimatedCost, data.estimatedCost || ''],
        [FP.phase, data.phase || 'subapplication'],
      ];
      const res = await createRecord(TP, fields.slice(0, 10), getToken()) as any;
      if (!res) return null;
      if (res && !res.Rid && res.recordId) res.Rid = res.recordId;
      const rid = res.Rid;

      // PUT remaining fields
      const remaining = ([
        [FP.status, data.status || 'draft'],
        [FP.federalSharePct, data.federalSharePct || '75'],
        [FP.projectAddress, data.projectAddress || ''],
        [FP.projectCity, data.projectCity || ''],
        [FP.projectState, data.projectState || ''],
        [FP.projectZip, data.projectZip || ''],
        [FP.notes, data.notes || ''],
      ] as [string, string][]).filter(f => f[1]);
      if (remaining.length > 0) {
        await updateRecord(TP, rid, remaining, getToken());
      }

      await get().fetchProjects();
      return get().projects.find(p => p.Rid === rid) || null;
    } catch (e) {
      console.error('HMGP createProject error:', e);
      return null;
    }
  },

  // ── Update HMGP project ──
  updateProject: async (rid, data) => {
    try {
      const fields: [string, string][] = [];
      if (data.projectTitle !== undefined) fields.push([FP.projectTitle, data.projectTitle]);
      if (data.projectType !== undefined) fields.push([FP.category, data.projectType]);
      if (data.problemDescription !== undefined) fields.push([FP.damageDescription, data.problemDescription]);
      if (data.scopeOfWork !== undefined) fields.push([FP.scopeOfWork, data.scopeOfWork]);
      if (data.estimatedCost !== undefined) fields.push([FP.estimatedCost, data.estimatedCost]);
      if (data.federalSharePct !== undefined) fields.push([FP.federalSharePct, data.federalSharePct]);
      if (data.phase !== undefined) fields.push([FP.phase, data.phase]);
      if (data.status !== undefined) fields.push([FP.status, data.status]);
      if (data.notes !== undefined) fields.push([FP.notes, data.notes]);
      if (data.organizationId !== undefined) fields.push([FP.organizationId, data.organizationId]);
      if (fields.length > 0) {
        await updateRecord(TP, rid, fields, getToken());
      }
      await get().fetchProjects();
    } catch (e) {
      console.error('HMGP updateProject error:', e);
    }
  },

  // ── Delete HMGP project ──
  deleteProject: async (rid) => {
    try {
      await deleteRecord(TP, rid, getToken());
      set(s => ({ projects: s.projects.filter(p => p.Rid !== rid) }));
    } catch (e) {
      console.error('HMGP deleteProject error:', e);
    }
  },

  // ── Subapplication CRUD (KV config interim) ──
  createSubapplication: async (data) => {
    try {
      const subs = [...get().subapplications];
      const newId = subs.length > 0 ? Math.max(...subs.map(s => s.Rid)) + 1 : 1;
      const now = new Date().toISOString().split('T')[0];
      const newSub: HMGPSubapplication = {
        id: newId, Rid: newId,
        organizationId: data.organizationId || '',
        disasterId: data.disasterId || '',
        subappNumber: data.subappNumber || `HMGP-SA-${String(newId).padStart(4, '0')}`,
        subappTitle: data.subappTitle || '',
        projectType: data.projectType || '',
        problemStatement: data.problemStatement || '',
        proposedActivity: data.proposedActivity || '',
        estimatedCost: data.estimatedCost || '',
        federalRequest: data.federalRequest || '',
        nonFederalMatch: data.nonFederalMatch || '',
        matchSource: data.matchSource || '',
        projectAddress: data.projectAddress || '',
        projectCity: data.projectCity || '',
        projectCounty: data.projectCounty || '',
        projectState: data.projectState || '',
        projectLat: data.projectLat || '',
        projectLng: data.projectLng || '',
        bcaStatus: data.bcaStatus || 'not_started',
        bcaRatio: data.bcaRatio || '',
        statePriorityScore: data.statePriorityScore || '',
        statePriorityRank: data.statePriorityRank || '',
        submittedDate: data.submittedDate || '',
        submittedBy: data.submittedBy || '',
        reviewerName: data.reviewerName || '',
        reviewDate: data.reviewDate || '',
        reviewNotes: data.reviewNotes || '',
        status: data.status || 'draft',
        linkedProjectId: data.linkedProjectId || '',
        notes: data.notes || '',
        creatorId: data.creatorId || '',
        createdDate: now,
      };
      subs.push(newSub);
      await _saveSubapps(subs);
      set({ subapplications: subs });
      return newSub;
    } catch (e) {
      console.error('HMGP createSubapplication error:', e);
      return null;
    }
  },

  updateSubapplication: async (rid, data) => {
    try {
      const subs = get().subapplications.map(s =>
        s.Rid === rid ? { ...s, ...data } : s
      );
      await _saveSubapps(subs);
      set({ subapplications: subs });
    } catch (e) {
      console.error('HMGP updateSubapplication error:', e);
    }
  },

  deleteSubapplication: async (rid) => {
    try {
      const subs = get().subapplications.filter(s => s.Rid !== rid);
      await _saveSubapps(subs);
      set({ subapplications: subs });
    } catch (e) {
      console.error('HMGP deleteSubapplication error:', e);
    }
  },
}));

// ── KV persistence helper ────────────────────────────────────
async function _saveSubapps(subs: HMGPSubapplication[]) {
  await fetch(
    `${WORKER_URL}/api/config/${SUBAPP_CONFIG_KEY}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Origin': 'https://apps.opengov.com' },
      body: JSON.stringify(subs),
    }
  );
}
